<template>
  <DefaultModal @close="closeModal">
    <template #header>
      <DefaultModalTitle>Uppladdade filer</DefaultModalTitle>
    </template>

    <template #body>
      <ErrorModal v-if="deleteFileError" @close="deleteFileError = false" />
      <ErrorModal
        v-else-if="!isUploadingFiles && failedUploads.length > 0"
        title="Notera"
        :message="`Det gick inte att ladda upp ${readableFileNames(
          failedUploads
        )}.`"
        @close="failedUploads = []"
      />
      <DeleteFileModal
        v-else-if="fileToBeDeleted"
        :file-name="fileToBeDeleted.fileName"
        :is-uploader="fileToBeDeleted.uploadedBy === account.userId"
        @confirm="deleteFile(fileToBeDeleted.fileName)"
        @close="fileToBeDeleted = null"
      />
      <FilesNotAcceptedModal
        v-else-if="notAcceptedModalVisible"
        :files="acceptedFiles"
        @close="notAcceptedModalVisible = false"
      />
      <FilePreviewModal
        v-else-if="showFilePreviewModal"
        :title="current.title"
        :mime="current.mime"
        :url="fileUrl"
        :application-id="applicationId"
        @close="showFilePreviewModal = false"
      />
      <div class="wrapper">
        <div v-if="isUploadingFiles" class="spinner-container">
          <div class="spinner-box">
            <div class="spinner">
              <BaseSpinner />
            </div>
            <p>Laddar upp {{ uploadedFilesInfo }} filer</p>
          </div>
        </div>
        <section class="upload-container">
          <input
            id="file"
            ref="fileInput"
            multiple
            hidden
            :disabled="isUploadingFiles"
            :accept="acceptedFiles"
            type="file"
            @change="checkFilesBeforeUpload($event.target.files)"
          />
          <label
            for="file"
            class="upload-area"
            :class="{ dragging: dragging, disabled: isUploadingFiles }"
            draggable
            @dragover.prevent="dragging = true"
            @dragleave.prevent="dragging = false"
            @drop.prevent="
              dragging = false;
              checkFilesBeforeUpload($event.dataTransfer.files);
            "
          >
            <BaseImage
              :url="svgs.ICONS.ADD_DOCUMENT"
              class="upload-image no-pointer-events"
            />
            <p class="upload-text no-pointer-events">
              Släpp filen här eller klicka för att ladda upp
              <span class="text-light">({{ readableAcceptedFiles }})</span>
            </p>
          </label>
        </section>
        <div
          v-if="filesFromId.length"
          class="file-browser-wrapper"
          :class="{ 'is-loading': isUploadingFiles }"
        >
          <div>
            <transition-group
              v-for="file in filesFromId"
              :key="file.fileName"
              tag="div"
              name="shuffle"
              appear
            >
              <div :key="file.fileName" style="position: relative">
                <FolderRow
                  :class="{
                    isRemoving: filesBeingRemoved.includes(file.fileName)
                  }"
                  tag="button"
                  type="button"
                  @click="
                    preview(
                      file.fileName,
                      file.fileName,
                      docTypeUtils.getFileExtension(file.fileName)
                    )
                  "
                >
                  <BaseImage
                    :url="docTypeUtils.getFileIcon(file.fileName)"
                    class="icon"
                  ></BaseImage>
                  <div class="folder-info">
                    <div class="folder-name">
                      {{ docTypeUtils.getFilename(file.fileName) }}
                    </div>
                    <div class="file-meta">
                      <span class="file-type">{{
                        docTypeUtils.getFileExtension(file.fileName)
                      }}</span
                      >,
                      {{ docTypeUtils.getHumanFileSize(file.fileSize) }}
                      /
                      <span class="file-link download-link">
                        Förhandsgranska
                      </span>
                    </div>
                  </div>
                </FolderRow>
                <button
                  class="delete-link"
                  type="button"
                  :disabled="filesBeingRemoved.includes(file.fileName)"
                  @click="fileToBeDeleted = file"
                >
                  <BaseImage :url="svgs.ICONS.TRASH" class="delete-link-img" />
                </button>
              </div>
            </transition-group>
          </div>
        </div>
      </div>
      <p class="info-text">
        OBS! Filer som laddas upp är endast synliga för denna hyresvärd.
      </p>
    </template>

    <template #footer>
      <!-- <a v-if="filesFromId.length > 0" :href="getZipFile">
        <BaseButton
          v-if="filesFromId.length > 0"
          :is-rounded="true"
          :icon="svgs.ICONS.DOWNLOAD"
        >
          Ladda ner som ZIP
        </BaseButton>
      </a> -->
      <BaseButton :is-rounded="true" :is-default="true" @click="closeModal"
        >Tillbaka</BaseButton
      >
    </template>
  </DefaultModal>
</template>

<script>
import { apiEndpoint } from '@/services/apiInstance';
import { mapActions, mapState } from 'vuex';

import docTypeUtils from '@/utils/docs-utils';
import DefaultModal from '@/components/DefaultModal.vue';
import BaseButton from '@/components/BaseButton.vue';
import svgs from '@/utils/icon-constants';
import BaseImage from '@/components/BaseImage.vue';
import BaseSpinner from '@/components/BaseSpinner.vue';
import DeleteFileModal from '@/components/DeleteFileModal.vue';
import ErrorModal from '@/components/ErrorModal.vue';
import FilesNotAcceptedModal from '@/components/FilesNotAcceptedModal.vue';
import DefaultModalTitle from '@/components/DefaultModalTitle.vue';
import FilePreviewModal from '@/components/FilePreviewModal.vue';
import FolderRow from '@/components/FolderRow.vue';

export default {
  components: {
    DefaultModal,
    BaseButton,
    BaseImage,
    BaseSpinner,
    DeleteFileModal,
    ErrorModal,
    FilesNotAcceptedModal,
    DefaultModalTitle,
    FilePreviewModal,
    FolderRow
  },

  props: {
    applicationId: {
      type: String,
      default: null
    }
  },
  emits: ['close'],

  data() {
    return {
      svgs,
      docTypeUtils,
      isUploadingFiles: false,
      failedUploads: [],
      fileToBeDeleted: null,
      uploadedFilesInfo: '',
      acceptedFiles: [
        'application/pdf',
        'image/jpeg',
        'image/gif',
        'image/png',
        'image/heic'
      ],
      dragging: false,
      notAcceptedModalVisible: false,
      deleteFileError: false,
      filesBeingRemoved: [],
      showFilePreviewModal: false,
      current: { id: '', title: '', mime: '' }
    };
  },
  computed: {
    ...mapState({
      files: state => state.landlordFiles.files,
      account: state => state.app.account
    }),
    readableAcceptedFiles() {
      return this.acceptedFiles.map(type => type.split('/')[1]).join(', ');
    },
    filesFromId() {
      return this.files[this.applicationId] || [];
    },
    fileUrl() {
      return `${apiEndpoint}/landlords/file/${this.applicationId}/${this.current.id}`;
    }
    // getZipFile() {
    //   return `${apiEndpoint}/documents/zip/${this.applicationId}`;
    // }
  },

  methods: {
    ...mapActions({
      addLandlordFile: 'landlordFiles/addFile',
      removeLandlordFile: 'landlordFiles/removeFile'
    }),
    preview(id, title, mime) {
      this.current = { id, title, mime: mime || '' };
      this.showFilePreviewModal = true;
    },
    closeModal() {
      this.$emit('close');
    },
    readableFileNames(fileNames) {
      let namesStr = fileNames[0];

      for (let i = 1; i < fileNames.length; i++) {
        const currentName = fileNames[i];
        if (i === fileNames.length - 1) {
          namesStr += ` och ${currentName}`;
        } else {
          namesStr += `, ${currentName}`;
        }
      }
      return namesStr;
    },

    async deleteFile(fileName) {
      this.fileToBeDeleted = null;
      this.filesBeingRemoved.push(fileName);

      try {
        await this.removeLandlordFile({
          applicationId: this.applicationId,
          fileName
        });
      } catch {
        this.deleteFileError = true;
      }

      this.filesBeingRemoved = this.filesBeingRemoved.filter(
        name => name !== fileName
      );
    },
    checkFilesBeforeUpload(files) {
      const notAccepted = [...files].some(
        file => !this.acceptedFiles.includes(file.type)
      );
      if (notAccepted) {
        this.notAcceptedModalVisible = true;
      } else {
        this.uploadFiles(files);
      }
    },
    async uploadFiles(files) {
      this.isUploadingFiles = true;

      // Upload in parallel
      await this.promisesProgress(
        [...files].map(async file => {
          try {
            await this.addLandlordFile({
              applicationId: this.applicationId,
              file
            });
          } catch {
            this.failedUploads.push(file.name);
          }
        }),
        completedAmount => {
          this.uploadedFilesInfo = `${completedAmount} / ${[...files].length}`;
        }
      );
      this.isUploadingFiles = false;
      this.uploadedFilesInfo = '';
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = null;
      }
    },
    promisesProgress(promises, callback) {
      let count = 0;
      callback(0);
      for (const promise of promises) {
        promise.then(() => {
          count++;
          callback(count);
        });
      }
      return Promise.all(promises);
    }
  }
};
</script>

<style scoped>
.wrapper {
  position: relative;
  min-height: 90px;
}
.file-browser-wrapper {
  border: 1px solid #e8e8e8;
  border-radius: 5px;
  overflow: hidden;
  position: relative;
  z-index: 1;
  margin-top: 2.5rem;
}
.file-browser-wrapper.is-loading {
  filter: blur(3px);
  opacity: 0.2;
}
.icon {
  display: inline-flex;
  height: 36px;
  width: 45px;
  margin-right: 12px;
}

.folder-info {
  font-weight: 600;
  width: 100%;
}
.folder-name {
  display: block;
  color: var(--color-dark-blue);
  font-size: 1.8rem;
  font-weight: 600;
}
.file-meta {
  display: block;
  font-size: 1.4rem;
  color: #878b9d;
  font-weight: 600;
  line-height: 17px;
}
.file-meta .file-type {
  text-transform: capitalize;
}
.file-meta .file-link {
  text-decoration: underline;
  background: none;
  border: none;
  box-shadow: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  font-weight: 600;
}
.delete-link {
  padding: 0;
  margin: 0;
  box-shadow: none;
  border: none;
  background: none;
  position: absolute;
  top: 8px;
  right: 8px;
  cursor: pointer;
}
.delete-link .delete-link-img {
  height: 17px;
  width: 16px;
}
.file-meta .download-link {
  color: var(--color-blue);
}
.spinner-container {
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 2;
  cursor: not-allowed;
}
.spinner-box {
  text-align: center;
}
.spinner-box p {
  margin-top: 2rem;
  color: var(--color-dark-blue);
  font-size: 1.6rem;
  font-weight: 400;
  line-height: 1.6;
}
.spinner {
  height: 4rem;
  width: 4rem;
  display: inline-block;
}
#spinner {
  border-top-color: var(--color-blue);
}
.upload-image {
  height: 40px;
  width: 32px;
}
input::-webkit-file-upload-button {
  visibility: hidden;
}
.upload-area {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 200px;
  padding: 20px;
  border: 3px dashed var(--color-blue);
  text-align: center;
  line-height: 1.5;
  font-weight: 600;
  border-radius: 20px;
  cursor: pointer;
  background-color: #e9efff;
  font-size: 14px;
}
.upload-area.disabled {
  cursor: not-allowed;
  opacity: 0.4;
}
.text-light {
  color: #a0a0a0;
}
.upload-text {
  margin-top: 20px;
  width: 155px;
}
.dragging {
  background-color: #c9d8ff;
}
.info-text {
  padding-top: 5px;
}
</style>
